import React, { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { 
  Users, 
  UserPlus, 
  Mail, 
  Shield, 
  Edit, 
  Trash2, 
  CheckCircle,
  AlertCircle,
  XCircle,
  MoreVertical,
  RefreshCw
} from "lucide-react"
import { toast } from 'sonner'
import { apiService } from '@/lib/api'
import { useAuth } from '@/contexts/auth-context'
import { getCompanyId } from '@/lib/config'
import { Building2 } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

interface CompanyUser {
  id: string
  userId: string
  email: string
  name: string
  globalRole: string
  companyRole: string
  grantedAt: string
  companyId: string
}

interface CompanyAccessPermissions {
  canManageAccess: boolean
  hasAccess: boolean
  role: string | null
  companyId: string
  companyName: string
}

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin', description: 'Full access to manage company and users' },
  { value: 'accountant', label: 'Accountant', description: 'Can manage finances and reports' },
  { value: 'manager', label: 'Manager', description: 'Can view and manage operations' },
  { value: 'employee', label: 'Employee', description: 'Basic access to company data' },
  { value: 'auditor', label: 'Auditor', description: 'Read-only access for auditing' },
  { value: 'viewer', label: 'Viewer', description: 'View-only access' },
]

// Filtered roles for company access - only admin and accountant
const COMPANY_ROLE_OPTIONS = ROLE_OPTIONS.filter(role => 
  role.value === 'admin' || role.value === 'accountant'
)

const getRoleBadgeVariant = (role: string) => {
  switch (role) {
    case 'admin':
      return 'default'
    case 'accountant':
      return 'secondary'
    case 'manager':
      return 'outline'
    case 'employee':
      return 'outline'
    case 'auditor':
      return 'secondary'
    case 'viewer':
      return 'outline'
    default:
      return 'outline'
  }
}

interface CompanyUserAccessProps {
  companyId?: string
}

export function CompanyUserAccess({ companyId: propCompanyId }: CompanyUserAccessProps) {
  const { user: currentUser, isAuthenticated, isLoading: authLoading } = useAuth()
  const queryClient = useQueryClient()
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<CompanyUser | null>(null)
  const [selectedRole, setSelectedRole] = useState<string>('accountant')
  
  // State for selected company ID - listens to header selection
  const [companyId, setCompanyId] = useState<string>(() => {
    const initialId = propCompanyId || getCompanyId()
    return initialId
  })
  
  // Use refs to avoid closure issues in event handlers and intervals
  const companyIdRef = useRef<string>(companyId)
  const lastCheckedCompanyIdRef = useRef<string>(companyId)
  
  // Keep refs in sync with state
  useEffect(() => {
    companyIdRef.current = companyId
    lastCheckedCompanyIdRef.current = companyId
  }, [companyId])

  // On mount, double-check localStorage to ensure we have the latest companyId
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedCompanyId = localStorage.getItem('company_id') || 
                               localStorage.getItem('companyId') || 
                               localStorage.getItem('company')
      
      if (storedCompanyId && storedCompanyId !== companyId) {
        setCompanyId(storedCompanyId)
        companyIdRef.current = storedCompanyId
        lastCheckedCompanyIdRef.current = storedCompanyId
      }
    }
  }, []) // Only run on mount
  
  // Listen for company changes from header
  useEffect(() => {
    // If prop companyId is provided, use it but still allow it to be overridden by localStorage
    // This allows the component to respond to header changes even when a prop is passed
    if (propCompanyId) {
      // Check if localStorage has a different company (user changed in header)
      const storedCompanyId = localStorage.getItem('company_id') || 
                               localStorage.getItem('companyId') || 
                               localStorage.getItem('company')
      
      // Use stored company if it exists and is different from prop (header selection takes precedence)
      const companyIdToUse = storedCompanyId && storedCompanyId !== propCompanyId ? storedCompanyId : propCompanyId
      
      if (companyIdToUse !== companyId) {
        setCompanyId(companyIdToUse)
        companyIdRef.current = companyIdToUse
        lastCheckedCompanyIdRef.current = companyIdToUse
      }
    }
    
    // Continue to listen for changes even if prop is provided
    
    // Always check localStorage on mount/update to ensure we're in sync
    const checkLocalStorage = () => {
      try {
        const storedCompanyId = localStorage.getItem('company_id') || 
                                 localStorage.getItem('companyId') || 
                                 localStorage.getItem('company')
        
        if (storedCompanyId && storedCompanyId !== companyIdRef.current) {
          companyIdRef.current = storedCompanyId
          lastCheckedCompanyIdRef.current = storedCompanyId
          setCompanyId(storedCompanyId)
        }
      } catch (error) {
        // Silent fail
      }
    }
    
    // Check immediately
    checkLocalStorage()
    
    // Storage event only fires in other tabs/windows, not the same window
    // So we rely on the interval check below for same-window changes
    const handleStorageChange = () => {
      try {
        const newCompanyId = getCompanyId()
        const currentCompanyId = companyIdRef.current
        if (newCompanyId && newCompanyId !== currentCompanyId) {
          // Clear cache for old company before switching
          queryClient.removeQueries({ queryKey: ['companyUsers', currentCompanyId] })
          queryClient.removeQueries({ queryKey: ['companyAccessPermissions', currentCompanyId] })
          
          // Invalidate new company's queries to force fresh fetch
          queryClient.invalidateQueries({ queryKey: ['companyUsers', newCompanyId] })
          queryClient.invalidateQueries({ queryKey: ['companyAccessPermissions', newCompanyId] })
          
          companyIdRef.current = newCompanyId
          setCompanyId(newCompanyId)
          // Reset forms when company changes
          setInviteForm(prev => ({ ...prev, selectedCompanyId: newCompanyId }))
          setCreateUserForm(prev => ({ ...prev, selectedCompanyId: newCompanyId }))
        }
      } catch (error) {
        // Silent fail
      }
    }
    
    // Listen for localStorage changes (works for other tabs)
    window.addEventListener('storage', handleStorageChange)
    
    // Also listen for custom events (header dispatches this)
    const handleCompanyChange = (e: CustomEvent) => {
      const newCompanyId = e.detail?.companyId
      const currentCompanyId = companyIdRef.current
      
      if (newCompanyId && newCompanyId !== currentCompanyId) {
        // Clear cache for old company before switching
        queryClient.removeQueries({ queryKey: ['companyUsers', currentCompanyId] })
        queryClient.removeQueries({ queryKey: ['companyAccessPermissions', currentCompanyId] })
        
        // Invalidate new company's queries to force fresh fetch
        queryClient.invalidateQueries({ queryKey: ['companyUsers', newCompanyId] })
        queryClient.invalidateQueries({ queryKey: ['companyAccessPermissions', newCompanyId] })
        
        lastCheckedCompanyIdRef.current = newCompanyId
        companyIdRef.current = newCompanyId
        setCompanyId(newCompanyId)
        // Reset forms when company changes
        setInviteForm(prev => ({ ...prev, selectedCompanyId: newCompanyId }))
        setCreateUserForm(prev => ({ ...prev, selectedCompanyId: newCompanyId }))
      }
    }
    
    window.addEventListener('companyChanged', handleCompanyChange as EventListener)
    
    // Also check on mount and periodically - this is the main way we detect changes
    // since storage events don't fire in the same window
    const interval = setInterval(() => {
      try {
        // Read directly from localStorage to get the latest value
        const storedCompanyId = localStorage.getItem('company_id') || 
                                 localStorage.getItem('companyId') || 
                                 localStorage.getItem('company')
        
        // Get current state from ref (always up-to-date)
        const currentStateCompanyId = companyIdRef.current
        
        if (storedCompanyId && storedCompanyId !== lastCheckedCompanyIdRef.current) {
          lastCheckedCompanyIdRef.current = storedCompanyId
          companyIdRef.current = storedCompanyId
          
          // Clear cache for old company before switching
          queryClient.removeQueries({ queryKey: ['companyUsers', currentStateCompanyId] })
          queryClient.removeQueries({ queryKey: ['companyAccessPermissions', currentStateCompanyId] })
          
          // Invalidate new company's queries to force fresh fetch
          queryClient.invalidateQueries({ queryKey: ['companyUsers', storedCompanyId] })
          queryClient.invalidateQueries({ queryKey: ['companyAccessPermissions', storedCompanyId] })
          
          setCompanyId(storedCompanyId)
          setInviteForm(prev => ({ ...prev, selectedCompanyId: storedCompanyId }))
          setCreateUserForm(prev => ({ ...prev, selectedCompanyId: storedCompanyId }))
        } else if (storedCompanyId && storedCompanyId !== currentStateCompanyId) {
          // Company changed but we already updated lastCheckedCompanyIdRef
          // This means state is out of sync
          setCompanyId(storedCompanyId)
        }
      } catch (error) {
        // Silent fail
      }
    }, 500) // Check every 500ms for faster response
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('companyChanged', handleCompanyChange as EventListener)
      clearInterval(interval)
    }
  }, [companyId, propCompanyId])
  
  // Fetch companies that the user can manage
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await apiService.getCompanies()
      return Array.isArray(response) ? response : (response?.data || [])
    },
    enabled: isAuthenticated && !authLoading
  })

  const companies = Array.isArray(companiesData) ? companiesData : []
  
  
  // Form state for inviting users
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'employee',
    message: '',
    selectedCompanyId: companyId || '', // Default to current company
  })
  
  // Form state for creating users
  const [createUserForm, setCreateUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'employee',
    companyRole: 'accountant',
    selectedCompanyId: companyId || '', // Default to current company
  })

  // Update form companyId when companyId changes
  useEffect(() => {
    if (companyId && !createUserDialogOpen) {
      setCreateUserForm(prev => ({ ...prev, selectedCompanyId: companyId }))
    }
  }, [companyId, createUserDialogOpen])

  // Reset form and set companyId when dialog opens
  useEffect(() => {
    if (createUserDialogOpen && companyId) {
      setCreateUserForm(prev => ({ ...prev, selectedCompanyId: companyId }))
    }
  }, [createUserDialogOpen, companyId])

  // Check if current user can manage access
  // Add refetch interval to check permissions periodically (in case role was updated)
  const { data: permissions, refetch: refetchPermissions } = useQuery<CompanyAccessPermissions>({
    queryKey: ['companyAccessPermissions', companyId],
    queryFn: async () => {
      const result = await apiService.getCompanyAccessPermissions(companyId)
      return result
    },
    enabled: !!companyId,
    refetchInterval: 30000, // Refetch every 30 seconds to catch role updates
    refetchOnWindowFocus: true, // Refetch when window regains focus
  })

  // Fetch company users - only for the selected company
  // Fetch users if we have a companyId - backend will handle authorization
  const { data: companyUsers, isLoading, error, refetch: refetchUsers } = useQuery<CompanyUser[]>({
    queryKey: ['companyUsers', companyId],
    queryFn: async () => {
      if (!companyId) {
        throw new Error('Company ID is required')
      }
      try {
        const users = await apiService.getCompanyUsers(companyId)
        // Ensure all returned users belong to this company
        const filteredUsers = Array.isArray(users) 
          ? users.filter((user: CompanyUser) => user.companyId === companyId)
          : []
        return filteredUsers
      } catch (err: any) {
        throw err
      }
    },
    enabled: !!companyId && isAuthenticated && !authLoading,
    staleTime: 0, // Always refetch when company changes
    gcTime: 0, // Don't cache across company changes
    retry: 1, // Retry once on failure
    refetchInterval: 10000, // Refetch every 10 seconds to catch newly created users
    refetchOnWindowFocus: true, // Refetch when window regains focus
  })

  // Force refetch when companyId changes
  useEffect(() => {
    if (companyId && isAuthenticated && !authLoading) {
      // Invalidate and refetch queries to force fresh fetch
      queryClient.invalidateQueries({ queryKey: ['companyUsers', companyId] })
      queryClient.invalidateQueries({ queryKey: ['companyAccessPermissions', companyId] })
    }
  }, [companyId, isAuthenticated, authLoading, queryClient])


  // Invite user mutation
  const inviteMutation = useMutation({
    mutationFn: async (data: { email: string; role: string; message?: string; companyId: string }) => {
      return await apiService.inviteUserToCompany(data.companyId, {
        email: data.email,
        role: data.role,
        message: data.message
      })
    },
    onSuccess: async (_, variables) => {
      toast.success('User invited successfully!')
      setInviteDialogOpen(false)
      setInviteForm({ email: '', role: 'accountant', message: '', selectedCompanyId: companyId || '' })
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['companyUsers', variables.companyId] })
      if (variables.companyId === companyId) {
        setTimeout(async () => {
          await queryClient.refetchQueries({ queryKey: ['companyUsers', companyId], exact: true })
          await refetchUsers()
        }, 1000)
      }
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to invite user')
    },
  })

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (data: { name: string; email: string; password: string; role: string; companyRole: string; companyId: string }) => {
      // Create the user with company role
      const user = await apiService.createUser({
        name: data.name,
        email: data.email,
        password: data.password,
        role: data.role,
        companyId: data.companyId,
        companyRole: data.companyRole, // Pass company role to backend
      })
      return user
    },
    onSuccess: async (_, variables) => {
      const defaultRole = currentUser?.role === 'admin' ? 'employee' : 'employee'
      setCreateUserDialogOpen(false)
      setCreateUserForm({ name: '', email: '', password: '', role: defaultRole, companyRole: 'accountant', selectedCompanyId: companyId || '' })
      
      // Invalidate queries first - invalidate for both the created company and current company
      queryClient.invalidateQueries({ queryKey: ['companyUsers', variables.companyId] })
      queryClient.invalidateQueries({ queryKey: ['users'] })
      
      // Always refetch the current company's users in case they match
      if (variables.companyId === companyId) {
        toast.success('User created successfully!')
        // Immediately invalidate and refetch
        queryClient.invalidateQueries({ queryKey: ['companyUsers', companyId] })
        // Small delay to ensure backend has processed the user creation, then refetch
        setTimeout(async () => {
          // Explicitly refetch the current company users
          await queryClient.refetchQueries({ queryKey: ['companyUsers', companyId], exact: true })
          // Also manually trigger refetch to ensure UI updates
          await refetchUsers()
        }, 1000)
      } else {
        // User was created for a different company
        const createdCompany = companies.find((c: any) => c.id === variables.companyId)
        toast.success(`User created successfully for ${createdCompany?.name || 'the selected company'}! Switch to that company to see the new user.`)
        // Still invalidate and refetch current company in case there's any cache issue
        queryClient.invalidateQueries({ queryKey: ['companyUsers', companyId] })
        setTimeout(async () => {
          await refetchUsers()
        }, 1000)
      }
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to create user')
    },
  })

  // Update user role mutation
  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      return await apiService.updateUserCompanyRole(companyId, userId, role)
    },
    onSuccess: () => {
      toast.success('User role updated successfully!')
      setEditDialogOpen(false)
      setSelectedUser(null)
      queryClient.invalidateQueries({ queryKey: ['companyUsers', companyId] })
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update user role')
    },
  })

  // Remove user mutation
  const removeUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return await apiService.removeUserFromCompany(companyId, userId)
    },
    onSuccess: () => {
      toast.success('User access removed successfully!')
      setDeleteDialogOpen(false)
      setSelectedUser(null)
      queryClient.invalidateQueries({ queryKey: ['companyUsers', companyId] })
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to remove user access')
    },
  })

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault()
    if (!inviteForm.email) {
      toast.error('Please enter an email address')
      return
    }
    if (!inviteForm.selectedCompanyId) {
      toast.error('Please select a company')
      return
    }
    inviteMutation.mutate({
      email: inviteForm.email,
      role: inviteForm.role,
      message: inviteForm.message || undefined,
      companyId: inviteForm.selectedCompanyId,
    })
  }

  const handleCreateUser = (e: React.FormEvent) => {
    e.preventDefault()
    if (!createUserForm.name || !createUserForm.email || !createUserForm.password) {
      toast.error('Please fill in all required fields')
      return
    }
    if (!createUserForm.selectedCompanyId) {
      toast.error('Please select a company')
      return
    }
    if (createUserForm.password.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }
    // Determine global role:
    // - System admins can set any global role
    // - Company admins can set 'accountant' or 'employee' (not 'admin')
    // - If making someone a company admin, default to 'accountant' for better permissions
    let globalRole = 'employee'
    if (currentUser?.role === 'admin') {
      // System admin can set any role
      globalRole = createUserForm.role
    } else if (createUserForm.companyRole === 'admin') {
      // If making someone a company admin, set global role to 'accountant' for better permissions
      // This ensures company admins can manage their companies properly
      globalRole = 'accountant'
    } else {
      // For other company roles, use 'employee'
      globalRole = 'employee'
    }
    
    const companyIdToUse = createUserForm.selectedCompanyId || companyId
      createUserMutation.mutate({
      name: createUserForm.name,
      email: createUserForm.email,
      password: createUserForm.password,
      role: globalRole,
      companyRole: createUserForm.companyRole,
      companyId: companyIdToUse,
    })
  }

  const handleEditRole = (user: CompanyUser) => {
    setSelectedUser(user)
    setSelectedRole(user.companyRole || 'accountant')
    setEditDialogOpen(true)
  }

  const handleUpdateRole = () => {
    if (!selectedUser) return
    updateRoleMutation.mutate({
      userId: selectedUser.userId,
      role: selectedRole,
    })
  }

  const handleDeleteClick = (user: CompanyUser) => {
    setSelectedUser(user)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (!selectedUser) return
    removeUserMutation.mutate(selectedUser.userId)
  }

  // Show loading state while checking permissions
  if (!companyId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Company Access Management
          </CardTitle>
          <CardDescription>
            Please select a company to manage user access.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  // Show loading state while checking permissions
  if (permissions === undefined) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Company Access Management
          </CardTitle>
          <CardDescription>Checking permissions...</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  // Show message if user doesn't have permission (but still show the card)
  if (!permissions?.canManageAccess) {
    const currentUserRole = currentUser?.role || 'employee';
    const isAccountant = currentUserRole === 'accountant';
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Company Access Management
          </CardTitle>
          <CardDescription>
            {isAccountant 
              ? "You need to be assigned as a company admin to manage user access for this company."
              : "You don't have permission to manage user access for this company. You need to be a company admin or global admin."
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground space-y-2">
            {isAccountant ? (
              <>
                <p>As an accountant, you can manage user access if you are assigned as a company admin for this company.</p>
                <p className="font-medium">Contact a global admin to assign you as a company admin for this company.</p>
              </>
            ) : (
              <>
                <p>Contact your system administrator to get admin access for this company.</p>
                <p className="text-xs text-blue-600 mt-2">
                  💡 <strong>Note:</strong> If you were just made a system admin, please log out and log back in for the changes to take effect.
                </p>
              </>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => refetchPermissions()}
              className="mt-2"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Permissions
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Company Users ({companyUsers?.filter((u: CompanyUser) => u.companyId === companyId)?.length || companyUsers?.length || 0})
              </CardTitle>
              <CardDescription>
                Manage user access and permissions for this company
                {(() => {
                  const currentCompany = companies.find((c: any) => c.id === companyId)
                  return currentCompany ? ` (${currentCompany.name})` : ''
                })()}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => refetchUsers()}
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="default">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create User
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>Create New User</DialogTitle>
                    <DialogDescription>
                      {currentUser?.role === 'admin' 
                        ? 'Create a new user account with login credentials.'
                        : 'Create a new user account. Global role will be set automatically based on company role.'
                      }
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="create-company">Select Company *</Label>
                      <Select
                        value={createUserForm.selectedCompanyId}
                        onValueChange={(value) => setCreateUserForm({ ...createUserForm, selectedCompanyId: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a company">
                            {companies.find((c: any) => c.id === createUserForm.selectedCompanyId)?.name || 'Select a company'}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {companies.length === 0 ? (
                            <SelectItem value="" disabled>No companies available</SelectItem>
                          ) : (
                            companies.map((company: any) => (
                              <SelectItem key={company.id} value={company.id}>
                                <div className="flex items-center gap-2">
                                  <Building2 className="h-4 w-4" />
                                  {company.name}
                                  {company.id === companyId && (
                                    <span className="text-xs text-muted-foreground ml-1">(Current)</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      {createUserForm.selectedCompanyId && createUserForm.selectedCompanyId !== companyId && (
                        <p className="text-xs text-amber-600 mt-1">
                          ⚠️ You're creating a user for a different company than the one currently selected. Switch to that company to see the new user after creation.
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="create-name">Full Name *</Label>
                      <Input
                        id="create-name"
                        placeholder="John Doe"
                        value={createUserForm.name}
                        onChange={(e) => setCreateUserForm({ ...createUserForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="create-email">Email Address *</Label>
                      <Input
                        id="create-email"
                        type="email"
                        placeholder="user@example.com"
                        value={createUserForm.email}
                        onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="create-password">Password *</Label>
                      <Input
                        id="create-password"
                        type="password"
                        placeholder="Minimum 6 characters"
                        value={createUserForm.password}
                        onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                        required
                        minLength={6}
                      />
                    </div>
                    {currentUser?.role === 'admin' && (
                      <div className="space-y-2">
                        <Label htmlFor="create-global-role">Global Role *</Label>
                        <Select
                          value={createUserForm.role}
                          onValueChange={(value) => setCreateUserForm({ ...createUserForm, role: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select global role" />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLE_OPTIONS.map((role) => (
                              <SelectItem key={role.value} value={role.value}>
                                {role.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {currentUser?.role !== 'admin' && (
                      <div className="space-y-2">
                        <Label htmlFor="create-global-role-info">Global Role</Label>
                        <div className="text-sm text-muted-foreground p-2 bg-muted rounded-md">
                          {createUserForm.companyRole === 'admin' 
                            ? 'Will be set to "accountant" (required for company admin permissions)'
                            : 'Will be set to "employee"'
                          }
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="create-company-role">Company Role *</Label>
                      <Select
                        value={createUserForm.companyRole}
                        onValueChange={(value) => setCreateUserForm({ ...createUserForm, companyRole: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select company role" />
                        </SelectTrigger>
                        <SelectContent>
                          {COMPANY_ROLE_OPTIONS.map((role) => (
                            <SelectItem key={role.value} value={role.value}>
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setCreateUserDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" disabled={createUserMutation.isPending}>
                        {createUserMutation.isPending ? 'Creating...' : 'Create User'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              
              {/* Invite by Email - Hidden for now */}
              {false && (
              <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Mail className="h-4 w-4 mr-2" />
                    Invite by Email
                  </Button>
                </DialogTrigger>
                <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite User to Company</DialogTitle>
                  <DialogDescription>
                    Send an invitation to a user to access a company.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleInvite} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite-company">Select Company *</Label>
                    <Select
                      value={inviteForm.selectedCompanyId}
                      onValueChange={(value) => setInviteForm({ ...inviteForm, selectedCompanyId: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a company">
                          {companies.find((c: any) => c.id === inviteForm.selectedCompanyId)?.name || 'Select a company'}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {companies.length === 0 ? (
                          <SelectItem value="" disabled>No companies available</SelectItem>
                        ) : (
                          companies.map((company: any) => (
                            <SelectItem key={company.id} value={company.id}>
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4" />
                                {company.name}
                              </div>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="user@example.com"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role *</Label>
                    <Select
                      value={inviteForm.role}
                      onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPANY_ROLE_OPTIONS.map((role) => (
                          <SelectItem key={role.value} value={role.value}>
                            <div>
                              <div className="font-medium">{role.label}</div>
                              <div className="text-xs text-muted-foreground">{role.description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Invitation Message (Optional)</Label>
                    <Textarea
                      id="message"
                      placeholder="Add a personal message to the invitation..."
                      value={inviteForm.message}
                      onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setInviteDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={inviteMutation.isPending}>
                      {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
                    </Button>
                  </DialogFooter>
                </form>
                </DialogContent>
              </Dialog>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading users...</div>
          ) : error ? (
            <div className="text-center py-8 text-destructive">
              <AlertCircle className="h-5 w-5 mx-auto mb-2" />
              <p className="font-medium mb-2">Failed to load users</p>
              <p className="text-sm text-muted-foreground mb-4">
                {(error as any)?.message || 'An error occurred while fetching users'}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => refetchUsers()}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          ) : !companyUsers || companyUsers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No users have access to this company yet.</p>
              <p className="text-sm mt-2">Invite users to get started.</p>
            </div>
          ) : (
            // Filter to ensure we only show users for the current company
            companyUsers.filter((user: CompanyUser) => user.companyId === companyId).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No users found for the selected company.</p>
                <p className="text-sm mt-2">The data may be from a different company. Please refresh.</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => refetchUsers()}
                  className="mt-2"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                      <TableHead>
                        Company Role
                        <span className="text-xs text-muted-foreground ml-1">(This Company)</span>
                      </TableHead>
                      <TableHead>
                        Global Role
                        <span className="text-xs text-muted-foreground ml-1">(System-Wide)</span>
                      </TableHead>
                  <TableHead>Access Granted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const filteredUsers = companyUsers?.filter((user: CompanyUser) => {
                    return user.companyId === companyId
                  }) || []
                  return filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.name || 'Unknown User'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        {user.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge variant={getRoleBadgeVariant(user.companyRole)}>
                          {user.companyRole}
                        </Badge>
                        {user.companyRole === 'admin' && (
                          <span className="text-xs text-muted-foreground">Company Admin</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge variant="outline">
                          {user.globalRole}
                        </Badge>
                        {user.globalRole === 'accountant' && user.companyRole !== 'admin' && (
                          <span className="text-xs text-blue-600">
                            Can be made company admin
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(user.grantedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleEditRole(user)}>
                            <Edit className="h-4 w-4 mr-2" />
                            {user.companyRole === 'admin' 
                              ? 'Change Company Role' 
                              : user.globalRole === 'accountant' 
                                ? 'Make Company Admin' 
                                : 'Change Role'}
                          </DropdownMenuItem>
                          {user.userId !== currentUser?.id && (
                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(user)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove Access
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  ))
                })()}
              </TableBody>
            </Table>
            )
          )}
        </CardContent>
      </Card>

      {/* Edit Role Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Company Role</DialogTitle>
            <DialogDescription>
              Update the company role for <strong>{selectedUser?.name || selectedUser?.email}</strong>
              <br />
              <span className="text-xs text-muted-foreground mt-1 block">
                Current: {selectedUser?.companyRole || 'accountant'} | Global: {selectedUser?.globalRole || 'employee'}
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>
                Company Role (This Company Only)
                <span className="text-xs text-muted-foreground ml-2">
                  (Safe: Only affects this company)
                </span>
              </Label>
              <Select
                value={selectedRole}
                onValueChange={setSelectedRole}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_ROLE_OPTIONS.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div>
                        <div className="font-medium">{role.label}</div>
                        <div className="text-xs text-muted-foreground">{role.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                {selectedUser?.globalRole === 'accountant' && (
                  <span className="text-blue-600 font-medium">
                    💡 Tip: Setting this to "admin" will make this accountant a company admin. 
                    They'll be able to manage users for this company, but won't become a system admin.
                  </span>
                )}
                {selectedUser?.globalRole !== 'accountant' && (
                  <span>
                    Setting this to "admin" makes them a company admin (can manage users for this company only), 
                    NOT a system admin. Their global role ({selectedUser?.globalRole || 'employee'}) remains unchanged.
                  </span>
                )}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditDialogOpen(false)
                setSelectedUser(null)
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateRole}
              disabled={updateRoleMutation.isPending || !selectedUser || selectedRole === selectedUser?.companyRole}
            >
              {updateRoleMutation.isPending ? 'Updating...' : 'Update Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove User Access?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove access for{' '}
              <strong>{selectedUser?.name || selectedUser?.email}</strong>? They will no longer
              be able to access this company's data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeUserMutation.isPending ? 'Removing...' : 'Remove Access'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

